import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { extractApiErrorMessage } from '@/lib/api-client';
import { SettlementFxClosePrompt } from '@/features/hedging';
import { useInvoicePayments } from '../api/use-invoice-payments';
import { useRecordPaymentMutation } from '../api/use-record-payment-mutation';
import { RecordForm } from './record-payment-form';
import type { InvoiceRow } from '../types';

const EPSILON = 0.01;

// A fully-paid Final Settlement is the trigger to close the order's FX cover.
function clearsFinalSettlement(invoice: InvoiceRow, amount: number, outstanding: number): boolean {
  // invoice_type arrives as the Prisma enum key ('Final_Settlement'); some
  // sources use the display label ('Final Settlement'). Accept both, as
  // invoice-label.ts and the trade dialog already do.
  const isFinalSettlement =
    invoice.invoice_type === 'Final_Settlement' || invoice.invoice_type === 'Final Settlement';
  return isFinalSettlement && invoice.order_id !== null && amount >= outstanding - EPSILON;
}

export interface RecordPaymentDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly onRecorded?: (() => void) | undefined;
}

function todayIso(): string {
  const now = new Date();
  const yyyy = String(now.getFullYear());
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function sumPayments(amounts: readonly (string | null)[]): number {
  return amounts.reduce<number>((acc, value) => {
    if (value === null) return acc;
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? acc + n : acc;
  }, 0);
}

function useOutstanding(invoice: InvoiceRow, open: boolean): number {
  const paymentsQuery = useInvoicePayments(invoice.id, open);
  return useMemo(() => {
    const total = invoice.total_amount === null ? 0 : Number.parseFloat(invoice.total_amount);
    const paid = sumPayments(paymentsQuery.data?.map((p) => p.total_amount_paid) ?? []);
    const diff = (Number.isFinite(total) ? total : 0) - paid;
    return diff > 0 ? Number(diff.toFixed(2)) : 0;
  }, [invoice.total_amount, paymentsQuery.data]);
}

function buildPayload(numericAmount: number, paidDate: string, bankRef: string, note: string) {
  return {
    total_amount_paid: Number(numericAmount.toFixed(2)),
    paid_date: paidDate,
    bank_reference: bankRef.trim() === '' ? undefined : bankRef.trim(),
    reference_note: note.trim() === '' ? undefined : note.trim(),
  };
}

function useFormFields(open: boolean, outstanding: number) {
  const [amount, setAmount] = useState('');
  const [paidDate, setPaidDate] = useState(todayIso());
  const [bankRef, setBankRef] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) {
      setAmount(outstanding > 0 ? outstanding.toFixed(2) : '');
      setPaidDate(todayIso());
      setBankRef('');
      setNote('');
    }
  }, [open, outstanding]);
  return { amount, setAmount, paidDate, setPaidDate, bankRef, setBankRef, note, setNote };
}

interface SubmitContext {
  mutation: ReturnType<typeof useRecordPaymentMutation>;
  invoice: InvoiceRow;
  amount: number;
  outstanding: number;
  fields: ReturnType<typeof useFormFields>;
  onRecorded?: (() => void) | undefined;
  onCleared: () => void;
  onClose: () => void;
}

function submitPayment(ctx: SubmitContext): void {
  ctx.mutation.mutate(
    buildPayload(ctx.amount, ctx.fields.paidDate, ctx.fields.bankRef, ctx.fields.note),
    {
      onSuccess: () => {
        toast.success('Payment recorded.');
        ctx.onRecorded?.();
        // Keep the component mounted (parent's `open` stays true) and swap to the
        // FX-close prompt; closing it returns control to the parent.
        if (clearsFinalSettlement(ctx.invoice, ctx.amount, ctx.outstanding)) ctx.onCleared();
        else ctx.onClose();
      },
      onError: (err) => {
        toast.error(extractApiErrorMessage(err, 'Failed to record the payment.'));
      },
    },
  );
}

interface PaymentEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: InvoiceRow;
  onRecorded?: (() => void) | undefined;
  onCleared: () => void;
}

function PaymentEntryDialog(props: Readonly<PaymentEntryDialogProps>) {
  const { open, onOpenChange, invoice, onRecorded, onCleared } = props;
  const outstanding = useOutstanding(invoice, open);
  const fields = useFormFields(open, outstanding);
  const mutation = useRecordPaymentMutation({
    invoiceId: invoice.id,
    orderId: invoice.order_id ?? '',
    blOrderId: invoice.bl_order_id ?? undefined,
  });
  const numericAmount = Number.parseFloat(fields.amount);
  const amountValid =
    Number.isFinite(numericAmount) && numericAmount > 0 && numericAmount <= outstanding + EPSILON;
  const canSubmit = amountValid && fields.paidDate.length === 10 && !mutation.isPending;
  const onSubmit = () => {
    submitPayment({
      mutation,
      invoice,
      amount: numericAmount,
      outstanding,
      fields,
      onRecorded,
      onCleared,
      onClose: () => {
        onOpenChange(false);
      },
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>
            Logs an inbound payment against {invoice.invoice_number ?? `invoice #${invoice.id}`}.
            Currency and direction are inherited from the invoice.
          </DialogDescription>
        </DialogHeader>
        <RecordForm
          state={{ outstanding, currency: invoice.currency, ...fields }}
          canSubmit={canSubmit}
          isPending={mutation.isPending}
          onCancel={() => {
            onOpenChange(false);
          }}
          onSubmit={onSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}

export function RecordPaymentDialog(props: RecordPaymentDialogProps) {
  const { open, onOpenChange, invoice, onRecorded } = props;
  const [fxPromptOpen, setFxPromptOpen] = useState(false);
  if (open && fxPromptOpen && invoice.order_id !== null) {
    return (
      <SettlementFxClosePrompt
        orderId={invoice.order_id}
        open
        onClose={() => {
          setFxPromptOpen(false);
          onOpenChange(false);
        }}
      />
    );
  }
  return (
    <PaymentEntryDialog
      open={open}
      onOpenChange={onOpenChange}
      invoice={invoice}
      onRecorded={onRecorded}
      onCleared={() => {
        setFxPromptOpen(true);
      }}
    />
  );
}
